import { ProductCardProduct } from 'shared/types/product/Product';
import { Variant } from 'shared/types/product/Variant';

/**
 * Normalize a color string for matching by removing spaces, hyphens, underscores, and slashes
 * @param color - The color string to normalize
 * @returns Normalized lowercase color string
 */
const normalizeColorString = (color: string): string => {
  return color.toLowerCase().replace(/[ \-_/]/g, '');
};

/**
 * Get the variant sale score, defaulting to 0 if not present
 * @param variant - The variant to get the sale score from
 * @returns The variant sale score or 0 if not present
 */
const getVariantSaleScore = (variant: Variant): number => {
  return variant.attributes?.variantSaleScore ?? 0;
};

/**
 * Sort variants by sale score in descending order (highest first)
 *
 * @param variants - Array of variants to sort
 * @returns New sorted array with highest sale score first
 *
 * @example
 * const sortedVariants = sortVariantsBySaleScore(product.variants);
 * const highestScoringVariant = sortedVariants[0];
 */
export const sortVariantsBySaleScore = (variants: Variant[]): Variant[] => {
  return [...variants].sort((a, b) => {
    const scoreA = getVariantSaleScore(a);
    const scoreB = getVariantSaleScore(b);
    return scoreB - scoreA; // Descending order (highest first)
  });
};

/**
 * Find the best in-stock variant when "Ready to Ship" filter is active
 *
 * Business Rules:
 * - If master variant is in stock, return it
 * - Otherwise, find the first in-stock variant with the highest variantSaleScore
 * - Only used when "Ready to Ship" facet is checked
 *
 * @param masterVariant - The product's master variant
 * @param variants - All product variants
 * @returns The best in-stock variant, or the master variant if none found
 *
 * @example
 * // When Ready to Ship filter is active and master variant is out of stock
 * const bestVariant = getInStockVariantWithHighestSaleScore(product.masterVariant, product.variants);
 * // Returns: First in-stock variant with highest variantSaleScore
 */
export const getInStockVariantWithHighestSaleScore = (
  masterVariant: Variant | undefined,
  variants: Variant[],
): Variant | undefined => {
  if (!masterVariant || !variants?.length) {
    return masterVariant;
  }

  if (masterVariant.isOnStock) {
    return masterVariant;
  }

  const inStockVariants = variants.filter((variant) => variant.isOnStock);

  if (inStockVariants.length === 0) {
    return masterVariant;
  }

  const sortedVariants = sortVariantsBySaleScore(inStockVariants);
  return sortedVariants[0];
};

/**
 * Match a variant from a target product based on the source variant's color attributes
 *
 * Matching Strategy (in priority order):
 * 1. Match by BOTH filterColor AND filterFinish
 * 2. Match by filterColor only
 * 3. Match by filterFinish only
 * 4. Fallback attempt: Match by actualColor
 *
 * @param sourceVariant - The variant to match from (e.g., currently selected variant on PDP)
 * @param targetProduct - The product to find a matching variant in (e.g., shop collection product)
 * @returns The matched variant's actualColor, or empty string if no match found
 *
 * @example
 * // On PDP, user selects variant with filterColor="bronze" and filterFinish="Nickel"
 * // Shop collection product has variants with same attributes
 * const matchedColor = matchVariantByColor(selectedVariant, shopCollectionProduct);
 * // Returns: The actualColor of the matched variant
 */
export const matchVariantByColor = (sourceVariant: Variant | undefined, targetProduct: ProductCardProduct): string => {
  if (!sourceVariant) return '';

  const filterColor = sourceVariant.attributes?.filterColor?.[0]?.key;
  const filterFinish = sourceVariant.attributes?.filterFinish?.[0]?.key;
  const actualColor = sourceVariant.attributes?.actualColor || '';

  if (filterColor && filterFinish) {
    const match = targetProduct.variants?.find(
      (variant) =>
        variant.attributes?.filterColor?.[0]?.key === filterColor &&
        variant.attributes?.filterFinish?.[0]?.key === filterFinish,
    );
    if (match) {
      return match.attributes?.actualColor || '';
    }
  }

  if (filterColor) {
    const match = targetProduct.variants?.find((variant) => variant.attributes?.filterColor?.[0]?.key === filterColor);
    if (match) {
      return match.attributes?.actualColor || '';
    }
  }

  if (filterFinish) {
    const match = targetProduct.variants?.find(
      (variant) => variant.attributes?.filterFinish?.[0]?.key === filterFinish,
    );
    if (match) {
      return match.attributes?.actualColor || '';
    }
  }

  if (actualColor) {
    const normalizedSourceColor = normalizeColorString(actualColor);
    const match = targetProduct.variants?.find((variant) => {
      const variantColor = variant.attributes?.actualColor || '';
      const normalizedVariantColor = normalizeColorString(variantColor);
      return normalizedVariantColor.includes(normalizedSourceColor);
    });
    if (match) {
      return match.attributes?.actualColor || '';
    }
  }

  return '';
};
